import { container } from '../utils/container/container';
import { FeatureFlagsLoader } from '../utils/feature-flags/feature-flags-loader';
import { ServiceIds } from '../utils/container/service-ids';
import { FlagNames } from '../utils/feature-flags/flag-names';
import {
    addFields,
    initialize,
    openSupport,
    setPlayer,
    getUnreadMessageCount,
    addEventListener as addTheymesEventListener,
    removeEventListener as removeTheymesEventListener
} from '@theymes/sdk';
import { CurrentUser } from '../general/modules/user/currentUser';
import { WidgetTheymesConfig } from '../types/supportWidget';
import { Player } from '@theymes/sdk/dist/types';
import { isGetUserFinished } from '../general/modules/getUser';

let initWidgetPromise: Promise<void> | null = null;
let destroyUnreadMessagesBadge: (() => void) | null = null;

function setPlayerInfo() {
    const playerInfo: Player = {
        email: CurrentUser.getInstance().getEmail(),
        name: window.app.user?.gameNickname || '',
        id: CurrentUser.getInstance().getSuid().toString()
    };

    setPlayer(playerInfo);
    addFields(getCustomFieldsForChat());
}


export function initWidgetTheymes() {
    if (initWidgetPromise) { return initWidgetPromise; }

    const config = widgetConfig();

    if (!config) { return null; }

    initWidgetPromise = (async() => {
        await isGetUserFinished();
        await initialize(config.token, config.domain, {
            buttonAction: 'support',
            mode: config.widgetType,
            position: 'bottom-right'
        });
        setPlayerInfo();
    })();

    return initWidgetPromise;
}

export async function showTheymesWidget(event: JQuery.Event) {
    event.preventDefault();

    if (!widgetTheymesIsOn()) { return; }
    if (!window.app.config.suid) { return; }

    await initWidgetTheymes();

    openSupportWidget();
}

function getCustomFieldsForChat() {
    return {
        account_email: CurrentUser.getInstance().getEmail(),
        account_id: CurrentUser.getInstance().getAccountId()
            .toString(),
        donate_amount: window.app.user?.donate,
        language: window.app.config.language,
        nickname: window.app.user?.gameNickname,
        payment_cohort: window.app.user?.paymentCohort,
        realm: window.app.config.user_realm,
        suid: CurrentUser.getInstance().getSuid()
            .toString(),
        suid_by_email: CurrentUser.getInstance().getSuid()
            .toString()
    };
}

export function openSupportWidget(language?: string) {
    if (!initWidgetPromise) { return; }
    // set data every time when the widget is opened, cause not all data is loaded in initWidgetTheymes()
    setPlayerInfo();
    openSupport({language: language || window.app.config.language || 'en' });
}

export function initUnreadMessagesBadge() {
    if (destroyUnreadMessagesBadge) {
        destroyUnreadMessagesBadge();
    }

    const chatIcon = document.querySelector('#minimized_chat') as HTMLElement | null;
    const chatMessagesCount = document.querySelector('#unread_chat_messages_count') as HTMLElement | null;

    if (!chatIcon || !chatMessagesCount) {
        return;
    }

    let chatIsOpened = false;

    const setVisible = (visible: boolean) => {
        chatIcon.style.display = visible ? 'block' : 'none';
    };

    const updateBadgeUnreadCount = (count = getUnreadMessageCount())=> {
        chatMessagesCount!.textContent = String(count);
        setVisible(count > 0 && !chatIsOpened);
    };

    const onOpen = () => {
        chatIsOpened = true;
        setVisible(false);
    };

    const onClose = () => {
        chatIsOpened = false;
        updateBadgeUnreadCount();
    };

    const onReadUpdate = (count: number) => {
        updateBadgeUnreadCount(count);
    };

    const onIconCLick = (event: Event) => {
        event.preventDefault();
        if (widgetTheymesIsOn()) {
            openSupportWidget();
        }
    };

    setVisible(false);

    initWidgetTheymes().then(() => {
        addTheymesEventListener('open', onOpen);
        addTheymesEventListener('close', onClose);
        addTheymesEventListener('unreadMessageCountUpdate', onReadUpdate);
        chatIcon.addEventListener('click', onIconCLick);

        destroyUnreadMessagesBadge = () => {
            removeTheymesEventListener('open', onOpen);
            removeTheymesEventListener('close', onClose);
            removeTheymesEventListener('unreadMessageCountUpdate', onReadUpdate);
            chatIcon.removeEventListener('click', onIconCLick);
            destroyUnreadMessagesBadge = null;
        };
    });
}

export function widgetTheymesIsOn() {
    const ffLoader = container.get<FeatureFlagsLoader>(ServiceIds.FEATURE_FLAG_LOADER);
    return ffLoader.get(FlagNames.WEB_CHAT_WIDGET_THEYMES).isOn();
}

function widgetConfig(): WidgetTheymesConfig {
    return window.app.config.theymesConfig;
}
